import { z } from 'zod/v4';
import {
  ALL_PERMISSIONS,
  LEGACY_PERMISSION_MAP,
  PERMISSION_CATEGORIES,
  PERMISSION_CATEGORY_LABELS,
  SYSTEM_PERMISSIONS,
  type PermissionCategory,
  type PermissionDefinition,
  type SystemPermission,
} from '@masters-club/types';

// ==================== Role Schemas ====================

export const createRoleSchema = z.object({
  displayName: z.string()
    .min(2, 'Название должно быть минимум 2 символа')
    .max(100, 'Название слишком длинное (максимум 100 символов)'),
  description: z.string().max(500, 'Описание слишком длинное (максимум 500 символов)').optional(),
  permissions: z.array(z.string()).optional(),
});

export const updateRoleSchema = createRoleSchema.partial();

export const roleIdParamSchema = z.object({
  id: z.coerce.number().int('ID должен быть целым числом').positive('ID должен быть положительным'),
});

export type CreateRoleDto = z.infer<typeof createRoleSchema>;
export type UpdateRoleDto = z.infer<typeof updateRoleSchema>;

export interface Role {
  id: number;
  name: string;
  displayName: string;
  description: string | null;
  permissions: string[];
  createdAt: Date;
  updatedAt: Date;
  _count?: {
    adminUsers: number;
    permissions: number;
  };
}

// ==================== Permission Schemas ====================

export const createPermissionSchema = z.object({
  code: z.string()
    .min(3, 'Код должен быть минимум 3 символа')
    .max(100, 'Код слишком длинный (максимум 100 символов)')
    .regex(/^[a-z_.]+$/, 'Код должен содержать только латинские буквы, точки и подчеркивания'),
  name: z.string()
    .min(2, 'Название должно быть минимум 2 символа')
    .max(100, 'Название слишком длинное (максимум 100 символов)'),
  description: z.string().max(500, 'Описание слишком длинное (максимум 500 символов)').optional(),
  category: z.string()
    .min(2, 'Категория должна быть минимум 2 символа')
    .max(50, 'Категория слишком длинная (максимум 50 символов)'),
});

export const updatePermissionSchema = createPermissionSchema.partial();

export const permissionIdParamSchema = z.object({
  id: z.coerce.number().int('ID должен быть целым числом').positive('ID должен быть положительным'),
});

export const permissionCategoryParamSchema = z.object({
  category: z.string().min(1, 'Категория обязательна'),
});

export type CreatePermissionDto = z.infer<typeof createPermissionSchema>;
export type UpdatePermissionDto = z.infer<typeof updatePermissionSchema>;

export interface Permission {
  id: number;
  code: string;
  name: string;
  description: string | null;
  category: string;
  createdAt: Date;
  updatedAt: Date;
}

// ==================== UserRole Schemas ====================

export const assignRoleSchema = z.object({
  userId: z.number().int('ID пользователя должен быть целым числом').positive('ID пользователя должен быть положительным'),
  roleId: z.number().int('ID роли должен быть целым числом').positive('ID роли должен быть положительным'),
});

export const userIdParamSchema = z.object({
  userId: z.coerce.number().int('ID пользователя должен быть целым числом').positive('ID пользователя должен быть положительным'),
});

export const userPermissionCheckParamSchema = z.object({
  userId: z.coerce.number().int('ID пользователя должен быть целым числом').positive('ID пользователя должен быть положительным'),
  permissionCode: z.string().min(1, 'Код права обязателен'),
});

export type AssignRoleDto = z.infer<typeof assignRoleSchema>;

export interface UserRole {
  id: number;
  userId: number;
  roleId: number;
  createdAt: Date;
  role?: Role;
}

// ==================== RolePermission Schemas ====================

export const assignPermissionToRoleSchema = z.object({
  roleId: z.number().int('ID роли должен быть целым числом').positive('ID роли должен быть положительным'),
  permissionId: z.number().int('ID права должен быть целым числом').positive('ID права должен быть положительным'),
});

export type AssignPermissionToRoleDto = z.infer<typeof assignPermissionToRoleSchema>;

// ==================== Permission Categories ====================

export { PERMISSION_CATEGORIES, PERMISSION_CATEGORY_LABELS };
export type { PermissionCategory };

// ==================== System Permissions ====================

export { SYSTEM_PERMISSIONS };
export type { SystemPermission };

// ==================== DECLARATIVE PERMISSIONS CONFIG ====================

export { ALL_PERMISSIONS };
export type { PermissionDefinition };

// ==================== LEGACY PERMISSION MIGRATION ====================

export { LEGACY_PERMISSION_MAP };

// ==================== DECLARATIVE ROLES CONFIG ====================

export interface RoleDefinition {
  name: string;
  displayName: string;
  description: string;
  /** Коды прав, которые назначаются этой роли */
  permissions: string[];
}

export const SYSTEM_ROLES: RoleDefinition[] = [
  {
    name: 'admin',
    displayName: 'Администратор',
    description: 'Полный доступ ко всем функциям системы',
    permissions: ALL_PERMISSIONS.map(p => p.code), // Все права
  },
  {
    name: 'manager',
    displayName: 'Менеджер',
    description: 'Доступ к CRM, заказам, рассылкам и базовой аналитике',
    permissions: [
      'users.read',
      'broadcasts.read',
      'broadcasts.create',
      'broadcasts.update',
      'broadcasts.send',
      'broadcasts.preview',
      'broadcasts.cancel',
      'analytics.view',
      'analytics.export',
      'knowledge.read',
      'support.read',
      'support.respond',
      'support.close',
      'crm.read',
      'crm.write',
      'crm.sync',
      'crm.leads.read',
      'crm.leads.manage',
      'leads.read',
      'leads.write',
      'managers.read',
      'orders.read',
      'orders.update',
      'orders.assign',
      'orders.status.change',
      'orders.notes.read',
      'orders.notes.write',
      'orders.history.read',
      'payments.read',
      'payments.refund',
      'subscriptions.read',
      'subscriptions.cancel',
      'audience.read',
      'audience.preview',
      'tags.read',
      'tags.assign',
      'user-invitations.read',
      'user-invitations.write',
      'user-invitations.send',
      'channel-memberships.read',
      'channel-memberships.send',
      'channel-memberships.resend',
      'dashboard.read',
      'search.read',
    ],
  },
  {
    name: 'analyst',
    displayName: 'Аналитик',
    description: 'Доступ только к аналитике и отчётам',
    permissions: [
      'analytics.view',
      'analytics.export',
      'staff-analytics.read',
      'staff-analytics.export',
      'livestream.analytics',
      'reporting.read',
      'dashboard.read',
      'audit-logs.read',
    ],
  },
  {
    name: 'support',
    displayName: 'Поддержка',
    description: 'Доступ к тикетам, чатам, базе знаний и приглашениям',
    permissions: [
      'users.read',
      'support.read',
      'support.respond',
      'support.close',
      'knowledge.read',
      'chat.read',
      'chat.write',
      'chat.warn',
      'chat.mute',
      'chat.unmute',
      'chat-moderation.read',
      'chat-moderation.write',
      'violations.read',
      'violations.warn',
      'violations.mute',
      'violations.unmute',
      'reports.read',
      'reports.write',
      'user-invitations.read',
      'user-invitations.send',
      'orders.read',
      'orders.notes.read',
      'orders.notes.write',
      'access.read',
      'dashboard.read',
      'search.read',
    ],
  },
  {
    name: 'content_manager',
    displayName: 'Контент-менеджер',
    description: 'Управление контентом: курсы, книги, рассылки, эфиры, база знаний',
    permissions: [
      'broadcasts.read',
      'broadcasts.create',
      'broadcasts.update',
      'broadcasts.delete',
      'broadcasts.send',
      'broadcasts.cancel',
      'broadcasts.preview',
      'broadcasts.manage',
      'knowledge.read',
      'knowledge.write',
      'knowledge.delete',
      'content.read',
      'content.write',
      'content-tags.read',
      'content-tags.write',
      'courses.read',
      'courses.create',
      'courses.update',
      'courses.delete',
      'courses.publish',
      'courses.restore',
      'books.read',
      'books.create',
      'books.update',
      'books.delete',
      'books.publish',
      'livestream.host',
      'livestream.manage',
      'livestream.analytics',
      'livestreams.read',
      'livestreams.create',
      'livestreams.update',
      'livestreams.delete',
      'livestreams.publish',
      'livestreams.unpublish',
      'livestreams.cancel',
      'livestreams.preview',
      'breakout-rooms.read',
      'breakout-rooms.write',
      'breakout-rooms.preview',
      'tags.read',
      'tags.write',
      'tags.assign',
      'audience.read',
      'audience.preview',
      'email-templates.manage',
      'message-templates.read',
      'message-templates.write',
      'message-templates.preview',
      'notifications.read',
      'notifications.write',
      'triggers.read',
      'triggers.write',
      'daily-planner.read',
      'daily-planner.write',
      'daily-planner.publish',
      'club.read',
      'club.write',
      'social-moderation.read',
      'social-moderation.approve',
      'social-moderation.reject',
      'moderation.read',
      'moderation.write',
      'dashboard.read',
      'search.read',
    ],
  },
];
